import {
  ChangeEvent,
  HTMLAttributes,
  useEffect,
  useMemo,
  useState,
  type JSX,
  useCallback,
  useRef,
  forwardRef,
  Ref,
  RefAttributes,
} from "react";

import {
  AutocompleteOwnerState,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteValue,
} from "@mui/material/Autocomplete";
import { AutocompleteGetTagProps } from "@mui/material/useAutocomplete";
import classnames from "classnames";
import { FieldValues, useController } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../helpers/validators";
import { useFormInputRefs } from "../../../hooks/useFormInputRefs";
import { TruncatedTextWithTooltip } from "../../TruncatedTextWithTooltip/TruncatedTextWithTooltip";
import { DashboardCheckbox } from "../DashboardCheckbox/DashboardCheckbox";
import { DashboardIcon } from "../Icon";
import { DashboardTextField } from "../TextField/DashboardTextField";
import { useMergeClasses } from "../useMergeClasses";

import { AutocompletePaperProvider } from "./AutocompletePaperContext";
import { DefaultAutocompletePaper } from "./DefaultAutocompletePaper";
import { StyledAutocomplete } from "./styles";
import {
  AutocompleteOption,
  FormAutocompleteProps,
  BaseAutocompleteProps,
  AutocompleteSsrProps,
  zeffyAutocompleteClasses,
} from "./types";
import { useTagRendering } from "./useTagRendering";

import { useCurrentInputVibe } from "@/hooks/useCurrentInputVibe";
import { useValidators } from "@/hooks/useValidators";
import { ChevronDown, Cross as Close } from "@/icons/outlined";

function AutocompleteInner<
  Value,
  T extends FieldValues,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  control,
  rules,
  name,
  forwardedRef,
  ...rest
}: AutocompleteSsrProps<Value, Multiple, DisableClearable, FreeSolo, T>) {
  return control && name ? (
    <FormAutocomplete<T, Value, Multiple, DisableClearable, FreeSolo>
      {...rest}
      ref={forwardedRef}
      control={control}
      rules={rules}
      name={name}
    />
  ) : (
    <BaseAutocomplete<Value, Multiple, DisableClearable, FreeSolo> {...rest} name={name} ref={forwardedRef} />
  );
}

AutocompleteInner.displayName = "Autocomplete";

export const DashboardAutocompleteSsr = AutocompleteInner as <
  Value = string | AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  T extends FieldValues = FieldValues,
>(
  props: AutocompleteSsrProps<Value, Multiple, DisableClearable, FreeSolo, T>
) => JSX.Element;

function FormAutocompleteInner<
  Value = string | AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  T extends FieldValues = FieldValues,
>(
  {
    control,
    defaultValue,
    name,
    onChange,
    rules,
    noAsterisk,
    ...rest
  }: FormAutocompleteProps<T, Value, Multiple, DisableClearable, FreeSolo>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);
  const { field } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as never,
    rules: validators,
  });

  const handleChange = useCallback(
    (value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> | undefined) => {
      if (typeof value === "string") {
        return;
      }
      onChange?.(value);
      field.onChange(value);
      return value;
    },
    [field, onChange]
  );

  return (
    <BaseAutocomplete
      {...rest}
      inputRef={field.ref}
      onChange={handleChange}
      value={field.value as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>}
      ref={ref}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

FormAutocompleteInner.displayName = "FormAutocomplete";

const FormAutocomplete = forwardRef(FormAutocompleteInner) as unknown as <
  T extends FieldValues,
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  props: FormAutocompleteProps<T, Value, Multiple, DisableClearable, FreeSolo> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const isOptionEqualToValue = (option: string | AutocompleteOption, value: string | AutocompleteOption) =>
  typeof option === "string" || typeof value === "string" ? option === value : option.value === value?.value;

function BaseAutocompleteInner<
  Value = string | AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  {
    classes: externalClasses,
    vibe,
    value,
    disableAutocomplete,
    error,
    errorMessage,
    onChange,
    label,
    multiple,
    inputRef: externalInputRef,
    placeholder,
    "data-test": dataTest,
    autoFocus,
    hideDropdownIcon,
    hideClearIcon,
    checkbox = false,
    limitTags = 3,
    endAdornment,
    popupIcon,
    inputValue,
    name,
    disableCloseOnSelect = true,
    open,
    onOpen,
    onClose,
    PaperComponent,
    freeSolo,
    renderTags: externalRenderTags,
    onFocus,
    chipProps,
    ...rest
  }: BaseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>,
  ref: Ref<HTMLInputElement>
) {
  // If no PaperComponent is provided, we only call the onChange callback when the user clicks the apply button
  const usingSubmitPattern = !PaperComponent && Boolean(multiple);

  const currentInputVibe = useCurrentInputVibe(vibe);
  const classes = useMergeClasses(zeffyAutocompleteClasses, externalClasses);

  const [internalIsOpen, setInternalIsOpen] = useState(Boolean(open));

  // In a multiselect Autocomplete, want to persist the search value even after selection
  // Autocomplete component doesn't support this, so we need to manage it ourselves
  const [internalInputValue, setInternalInputValue] = useState(inputValue || "");

  // We maintain the values internally, and propagate them on apply
  const [syncedInternalValue, setSyncedInternalValue] = useState(value);
  const [internalValue, setInternalValue] = useState(value);

  // Ref to the actual input element for blurring
  const internalInputRef = useRef<HTMLInputElement>(null);

  // Combine both external and internal input refs
  const refCallBack = useFormInputRefs({ inputRefs: [internalInputRef, externalInputRef] });

  const hideTags = internalIsOpen && usingSubmitPattern && multiple;

  const { renderTags: internalRenderTags } = useTagRendering({
    tags: multiple ? (internalValue as (string | AutocompleteOption<string>)[]) || [] : [],
    hideTags,
    ignoreInputWidth: usingSubmitPattern && multiple,
    chipProps,
  });

  useEffect(() => {
    if (Boolean(open) !== internalIsOpen) {
      setInternalIsOpen(Boolean(open));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // Sync when value changes externally
    setSyncedInternalValue(value);
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    setInternalInputValue(inputValue || "");
  }, [inputValue]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (multiple) {
        setInternalInputValue((event.target as HTMLInputElement).value);
      }
    },
    [multiple]
  );

  const handleOnOpen = useCallback(() => {
    // We should only handle the open state internally if open is undefined
    if (open === undefined) {
      setInternalIsOpen(true);
    }
    onOpen?.();
  }, [onOpen, open]);

  const closeAutocomplete = useCallback(() => {
    if (multiple && !freeSolo) {
      setInternalInputValue("");
    }
    // We should only handle the open state internally if open is undefined
    if (open === undefined) {
      setInternalIsOpen(false);
    }
    onClose?.();
    setTimeout(() => {
      // Blur the actual input element
      internalInputRef.current?.blur();
    }, 0);
  }, [freeSolo, multiple, onClose, open]);

  const handleOnClose = useCallback(() => {
    setInternalValue(syncedInternalValue);
    closeAutocomplete();
  }, [syncedInternalValue, closeAutocomplete]);

  const handleOnApply = useCallback(() => {
    onChange?.(internalValue);
    setSyncedInternalValue(internalValue);
    closeAutocomplete();
  }, [closeAutocomplete, internalValue, onChange]);

  const handleOnClear = useCallback(() => {
    const newValue = multiple ? ([] as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>) : undefined;
    onChange?.(newValue);
    setSyncedInternalValue(newValue);
    setInternalValue(newValue);
    closeAutocomplete();
  }, [closeAutocomplete, multiple, onChange]);

  const handleOnRemoveTag = useCallback(
    (optionToRemove: string | AutocompleteOption) => {
      if (!multiple || !Array.isArray(internalValue)) {
        return;
      }

      const newValue = (internalValue as (string | AutocompleteOption)[]).filter((item) => {
        if (typeof item === "string" && typeof optionToRemove === "string") {
          return item !== optionToRemove;
        }
        if (typeof item === "object" && typeof optionToRemove === "object") {
          return item.value !== optionToRemove.value;
        }
        return true; // Keep items that don't match the type being removed
      });

      setInternalValue(newValue as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>);
    },
    [multiple, internalValue]
  );

  const renderOption = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement>,
      option: string | AutocompleteOption,
      { selected }: { selected: boolean }
    ) => {
      const label = typeof option === "string" ? option : option.label;
      const key = typeof option === "string" ? option : (option.value as string);
      return (
        <li {...props} key={key}>
          {checkbox && (
            <DashboardCheckbox
              checked={selected}
              sx={(theme) => ({
                marginRight: theme.spacing(1),
              })}
              data-test="base-autocomplete-option-checkbox"
            />
          )}
          <TruncatedTextWithTooltip
            tooltipProps={{
              title: label,
              placement: "right",
              "data-test": "base-autocomplete-option-tooltip",
            }}
            data-test="base-autocomplete-option-label"
          >
            {label}
          </TruncatedTextWithTooltip>
        </li>
      );
    },
    [checkbox]
  );

  const inputClasses = useMemo(
    () => ({
      root: classnames(classes.inputRoot, { [classes.inputRootWithHiddenTags]: hideTags }),
      input: classnames(classes.input, { [classes.inputWithHiddenTags]: hideTags }),
    }),
    [classes.input, classes.inputRoot, classes.inputRootWithHiddenTags, classes.inputWithHiddenTags, hideTags]
  );

  const renderInput = useCallback(
    ({
      InputProps: { endAdornment: inputEndAdornment, ref, startAdornment },
      ...rest
    }: AutocompleteRenderInputParams) => (
      <DashboardTextField
        {...rest}
        {...(multiple ? { onChange: handleInputChange } : undefined)}
        vibe={currentInputVibe}
        classes={inputClasses}
        data-test={dataTest}
        disableAutocomplete={disableAutocomplete}
        endAdornment={
          <>
            {inputEndAdornment}
            {endAdornment}
          </>
        }
        error={error}
        errorMessage={errorMessage}
        label={label}
        ref={ref}
        startAdornment={startAdornment}
        inputRef={refCallBack}
        placeholder={placeholder}
        value={
          multiple
            ? internalInputValue
            : typeof value === "string"
              ? value
              : (value as AutocompleteOption<string>)?.value
        }
        autoFocus={autoFocus}
        name={name}
      />
    ),
    [
      autoFocus,
      currentInputVibe,
      dataTest,
      disableAutocomplete,
      endAdornment,
      error,
      errorMessage,
      handleInputChange,
      inputClasses,
      refCallBack,
      internalInputValue,
      label,
      multiple,
      name,
      placeholder,
      value,
    ]
  );

  return (
    <AutocompletePaperProvider
      multiple={multiple || false}
      internalValue={internalValue}
      syncedInternalValue={syncedInternalValue}
      internalIsOpen={internalIsOpen}
      usingSubmitPattern={usingSubmitPattern}
      externalRenderTags={
        externalRenderTags as (
          value: (string | AutocompleteOption<unknown>)[],
          getTagProps: AutocompleteGetTagProps
        ) => React.ReactNode
      }
      onApply={handleOnApply}
      onClear={handleOnClear}
      onRemoveTag={handleOnRemoveTag}
      chipProps={chipProps}
    >
      <StyledAutocomplete<Value, Multiple, DisableClearable, FreeSolo>
        {...rest}
        clearIcon={
          hideClearIcon ? null : (
            <DashboardIcon
              size="small"
              className={classnames({ [classes.iconForm]: currentInputVibe === "form" })}
              onClick={(e) => {
                e.stopPropagation();
                handleOnClear();
              }}
            >
              <Close />
            </DashboardIcon>
          )
        }
        popupIcon={
          hideDropdownIcon ? null : (
            <DashboardIcon size="small" className={classnames({ [classes.iconForm]: currentInputVibe === "form" })}>
              {popupIcon ?? <ChevronDown />}
            </DashboardIcon>
          )
        }
        renderInput={renderInput}
        classes={{
          root: classes.root,
          clearIndicator: classnames(classes.indicator, classes.clearIndicator),
          endAdornment: classes.endAdornment,
          option: classnames(classes.option, { [classes.optionForm]: currentInputVibe === "form" }),
          paper: classes.paper,
          popupIndicator: classnames(classes.indicator, classes.popupIndicator, {
            [classes.disablePopupIndicatorTransform]: popupIcon,
          }),
          listbox: classes.listBox,
        }}
        ref={ref}
        multiple={multiple}
        renderOption={
          (rest.renderOption || renderOption) as (
            props: React.HTMLAttributes<HTMLLIElement>,
            option: Value,
            state: AutocompleteRenderOptionState,
            ownerState: AutocompleteOwnerState<Value, Multiple, DisableClearable, FreeSolo>
          ) => React.ReactNode
        }
        renderTags={
          externalRenderTags ||
          (internalRenderTags as (value: Value[], getTagProps: AutocompleteGetTagProps) => React.ReactNode)
        }
        isOptionEqualToValue={
          rest.isOptionEqualToValue || (isOptionEqualToValue as (option: Value, value: Value) => boolean)
        }
        inputValue={multiple ? internalInputValue : undefined}
        disableCloseOnSelect={disableCloseOnSelect}
        open={internalIsOpen}
        onOpen={handleOnOpen}
        onClose={handleOnClose}
        onFocus={(event) => {
          handleOnOpen();
          onFocus?.(event);
        }}
        PaperComponent={PaperComponent || (usingSubmitPattern ? DefaultAutocompletePaper : undefined)}
        onChange={(_evt, newValue) => {
          if (!usingSubmitPattern) {
            onChange?.(newValue);
          } else {
            setInternalValue(newValue);
          }
        }}
        value={internalValue}
        limitTags={limitTags}
        freeSolo={freeSolo}
        data-test="base-autocomplete"
      />
    </AutocompletePaperProvider>
  );
}
BaseAutocompleteInner.displayName = "BaseAutocomplete";

const BaseAutocomplete = forwardRef(BaseAutocompleteInner) as unknown as <
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  props: BaseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> & RefAttributes<HTMLInputElement>
) => JSX.Element;
